import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  addContentCategory,
  updateContentCategory,
  getContentCategoryById,
} from "../../../services/contentCategoriesService";
import { validateContentCategory } from "../../../utils/validation";
import { generateSlug } from "../../../utils/cmsUtils";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { handleErrors } from "../../../utils/errorHandler";
import ComponentHeader from "../../Common/OtherElements/ComponentHeader";

const INITIAL_FORM_STATE = {
  categoryName: "",
  categorySlug: "",
  isPremium: false,
  displayOrder: "",
};

const INITIAL_ERRORS_STATE = {
  categoryName: "",
  categorySlug: "",
  displayOrder: "",
};

const mapCategoryResponse = (data) => ({
  categoryName: data.categoryName || data.CategoryName || "",
  categorySlug: data.categorySlug || data.CategorySlug || "",
  isPremium: Boolean(data.isPremium ?? data.IsPremium ?? false),
  displayOrder:
    data.displayOrder !== undefined && data.displayOrder !== null
      ? String(data.displayOrder)
      : data.DisplayOrder !== undefined && data.DisplayOrder !== null
      ? String(data.DisplayOrder)
      : "",
});

export const AddContentCategory = ({
  editMode = false,
  initialData = {},
  onSuccess,
  setSelectedCategory,
  setEditMode,
}) => {
  const [formData, setFormData] = useState(INITIAL_FORM_STATE);
  const [errors, setErrors] = useState(INITIAL_ERRORS_STATE);
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const slugManuallyEdited = useRef(false);

  useEffect(() => {
    const fetchData = async () => {
      const categoryId = initialData?.id ?? initialData?.Id;
      if (editMode && categoryId) {
        try {
          const data = await getContentCategoryById(categoryId);
          setFormData(mapCategoryResponse(data));
          slugManuallyEdited.current = true;
        } catch (error) {
          handleErrors(error);
        }
      } else {
        setFormData(INITIAL_FORM_STATE);
        slugManuallyEdited.current = false;
      }
    };
    fetchData();
  }, [editMode, initialData]);

  const handleInputChange = ({ target: { name, value } }) => {
    if (name === "categoryName") {
      setFormData((prev) => ({
        ...prev,
        categoryName: value,
        ...(slugManuallyEdited.current
          ? {}
          : { categorySlug: generateSlug(value) }),
      }));
    } else if (name === "categorySlug") {
      slugManuallyEdited.current = true;
      setFormData((prev) => ({ ...prev, categorySlug: value }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleIsPremiumChange = ({ target: { checked } }) => {
    setFormData((prev) => ({ ...prev, isPremium: checked }));
  };

  const handleDisplayOrderChange = (e) => {
    const { value } = e.target;
    if (value === "" || /^[1-9]\d*$/.test(value)) {
      setFormData((prev) => ({ ...prev, displayOrder: value }));
      setErrors((prev) => ({ ...prev, displayOrder: "" }));
    }
  };

  const handleDisplayOrderKeyDown = (e) => {
    if (["e", "E", "+", "-", "."].includes(e.key)) {
      e.preventDefault();
    }
  };

  const resetForm = () => {
    setFormData(INITIAL_FORM_STATE);
    setErrors(INITIAL_ERRORS_STATE);
    slugManuallyEdited.current = false;
  };

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      const { valid, errors: validationErrors } =
        validateContentCategory(formData);
      setErrors(validationErrors);

      if (!valid) return;

      const categoryId = initialData?.id ?? initialData?.Id;
      const payload = {
        categoryName: formData.categoryName.trim(),
        categorySlug: formData.categorySlug.trim(),
        isPremium: Boolean(formData.isPremium),
        displayOrder: Number(formData.displayOrder),
      };

      if (editMode) {
        payload.id = categoryId;
      }

      try {
        setIsButtonDisabled(true);
        const action = editMode ? updateContentCategory : addContentCategory;
        const response = await action(payload);

        if (response?.data?.isSuccess === false) {
          toast.error(
            response.data.message || "Request failed. Please try again."
          );
          return;
        }

        toast.success(
          `Content category ${editMode ? "updated" : "added"} successfully!`
        );
        resetForm();
        if (onSuccess) onSuccess();
        setEditMode(false);
      } catch (error) {
        handleErrors(error);
      } finally {
        setIsButtonDisabled(false);
      }
    },
    [formData, editMode, initialData, onSuccess, setEditMode]
  );

  const handleAddNewClick = () => {
    resetForm();
    setSelectedCategory(null);
    setEditMode(false);
  };

  return (
    <>
      <ComponentHeader title="Content Categories" />

      <div className="row">
        <div className="col-xxl-12">
          <div className="card mt-xxl-n5">
            <div className="card-header">
              <h5 className="mb-sm-1 mt-sm-1">
                {editMode ? "Update Content Category" : "Add Content Category"}
              </h5>
            </div>

            <div className="card-body p-4">
              <form onSubmit={handleSubmit} method="POST">
                <div className="row">
                  <div className="col-lg-3 col-md-4 col-sm-12">
                    <div className="mb-3">
                      <label htmlFor="categoryName" className="form-label">
                        Category Name{" "}
                        <span className="required-field">*</span>
                      </label>
                      <input
                        type="text"
                        name="categoryName"
                        id="categoryName"
                        placeholder="Enter Category Name"
                        value={formData.categoryName}
                        onChange={handleInputChange}
                        className={`form-control ${
                          errors.categoryName ? "is-invalid" : ""
                        }`}
                      />
                      {errors.categoryName && (
                        <div className="invalid-feedback">
                          {errors.categoryName}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="col-lg-3 col-md-4 col-sm-12">
                    <div className="mb-3">
                      <label htmlFor="categorySlug" className="form-label">
                        Category Slug{" "}
                        <span className="required-field">*</span>
                      </label>
                      <input
                        type="text"
                        name="categorySlug"
                        id="categorySlug"
                        placeholder="category-slug"
                        value={formData.categorySlug}
                        onChange={handleInputChange}
                        className={`form-control ${
                          errors.categorySlug ? "is-invalid" : ""
                        }`}
                      />
                      {errors.categorySlug && (
                        <div className="invalid-feedback">
                          {errors.categorySlug}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="col-lg-3 col-md-4 col-sm-12">
                    <div className="mb-3">
                      <label htmlFor="displayOrder" className="form-label">
                        Display Order{" "}
                        <span className="required-field">*</span>
                      </label>
                      <input
                        type="number"
                        name="displayOrder"
                        id="displayOrder"
                        min="1"
                        step="1"
                        placeholder="Enter Display Order"
                        value={formData.displayOrder}
                        onChange={handleDisplayOrderChange}
                        onKeyDown={handleDisplayOrderKeyDown}
                        className={`form-control ${
                          errors.displayOrder ? "is-invalid" : ""
                        }`}
                      />
                      {errors.displayOrder && (
                        <div className="invalid-feedback">
                          {errors.displayOrder}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="col-lg-3 col-md-4 col-sm-12">
                    <div className="mb-3">
                      <label className="form-label d-block">Is Premium</label>
                      <div className="form-check form-switch form-switch-custom form-switch-primary">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          role="switch"
                          id="isPremium"
                          checked={formData.isPremium}
                          onChange={handleIsPremiumChange}
                        />
                        <label className="form-check-label" htmlFor="isPremium">
                          Premium category
                        </label>
                      </div>
                    </div>
                  </div>

                  <div className="col-lg-12">
                    <div>
                      <button
                        type="submit"
                        className="btn btn-secondary pt-1 pb-1 p-3"
                        disabled={isButtonDisabled}
                      >
                        {isButtonDisabled
                          ? editMode
                            ? "Updating"
                            : "Saving"
                          : editMode
                          ? "Update"
                          : "Save"}
                      </button>
                      {editMode && (
                        <button
                          type="button"
                          onClick={handleAddNewClick}
                          className="btn btn-danger ms-1 pt-1 pb-1 p-3"
                        >
                          Cancel
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
